"use client";

import { LandingPageButton } from "@/components/landing-page/landing-page-button";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  AlertTriangleIcon,
  ChevronRightIcon,
  ShieldIcon,
  TrendingDownIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";
import useMeasure from "@/hooks/layout/use-measure";
import { motion, MotionConfig, Transition, Variants } from "motion/react";

const defaultTransition = {
  type: "spring",
  bounce: 0,
  duration: 0.5,
} satisfies Transition;

const opacityVariant = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { delay: 0.3 } },
  exit: { opacity: 0 },
} satisfies Variants;

export default function PortfolioRiskCalculator() {
  const t = useTranslations("Portfolio.risk.calculator");
  const [riskPercentage, setRiskPercentage] = React.useState([15]);
  const [showResults, setShowResults] = React.useState(false);
  const [portfolioValue, setPortfolioValue] = React.useState<number[]>([6]); // log10(1,000,000) = 6
  const [ref, measure] = useMeasure<HTMLDivElement>();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const logToValue = (logValue: number) => {
    return Math.pow(10, logValue);
  };

  const calculateLoss = () => {
    const portfolio = logToValue(portfolioValue[0]);
    return (portfolio * riskPercentage[0]) / 100;
  };

  const handleCalculate = () => {
    setShowResults(true);
  };

  return (
    <div className="container flex min-h-screen flex-col items-center gap-4 px-4 py-20 text-foreground md:px-6 md:py-32">
      {/* Hero Section */}
      <div className="mb-12 text-center">
        <div className="mb-6 flex justify-center">
          <div className="rounded-full bg-red-100 p-3">
            <AlertTriangleIcon className="size-8 text-red-600" />
          </div>
        </div>
        <h1 className="section-title mb-5 flex flex-wrap items-center justify-center gap-2 text-center text-3xl font-bold tracking-tighter text-foreground sm:text-4xl md:flex-row md:items-end md:text-6xl">
          {t("hero.title")}
        </h1>
        <p className="paragraph text-md sm:text-lg">{t("hero.subtitle")}</p>
      </div>

      {/* Interactive Calculator */}
      <Card className="mx-auto max-w-2xl">
        <CardHeader className="pb-6 text-center">
          <CardTitle className="text-2xl font-bold text-foreground">
            {t("card.title")}
          </CardTitle>
          <CardDescription className="text-lg text-muted-foreground">
            {t("card.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MotionConfig transition={defaultTransition}>
            <motion.div animate={{ height: measure.height }}>
              <div ref={ref} className="space-y-8">
                {/* Portfolio Size Input */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold text-foreground">
                    {t.rich("portfolioSize.label", {
                      amount: formatCurrency(logToValue(portfolioValue[0])),
                      strong: (chunks) => (
                        <span className="font-bold text-warning">{chunks}</span>
                      ),
                    })}
                  </Label>
                  <div className="px-2">
                    <Slider
                      value={portfolioValue}
                      onValueChange={(value) => {
                        setPortfolioValue(value);
                        setShowResults(false);
                      }}
                      max={9} // 10^9 = $1B
                      min={6} // 10^6 = $1M
                      step={0.1}
                      className="w-full"
                    />
                    <div className="mt-2 flex justify-between text-sm text-muted-foreground">
                      <span>{t("portfolioSize.minLabel")}</span>
                      <span>$10M</span>
                      <span>$100M</span>
                      <span>{t("portfolioSize.maxLabel")}</span>
                    </div>
                  </div>
                </div>

                {/* Risk Percentage Slider */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold text-foreground">
                    {t.rich("riskPercentage.label", {
                      percentage: riskPercentage[0],
                      strong: (chunks) => (
                        <span className="font-bold text-red-600">{chunks}</span>
                      ),
                    })}
                  </Label>
                  <div className="px-2">
                    <Slider
                      value={riskPercentage}
                      onValueChange={setRiskPercentage}
                      max={60}
                      min={1}
                      step={1}
                      className="w-full"
                    />
                    <div className="mt-2 flex justify-between text-sm text-muted-foreground">
                      <span>{t("riskPercentage.minLabel")}</span>
                      <span>{t("riskPercentage.maxLabel")}</span>
                    </div>
                  </div>
                </div>

                {/* Calculate Button */}
                <Button
                  onClick={handleCalculate}
                  variant="destructive"
                  className="h-12 w-full text-lg font-semibold"
                >
                  {t("calculate")}
                </Button>

                {/* Results */}
                {showResults ? (
                  <motion.div
                    variants={opacityVariant}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <Card className="border-red-200 bg-red-50">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <TrendingDownIcon className="mx-auto mb-4 h-12 w-12 text-red-600" />
                          <h3 className="mb-2 text-2xl font-bold text-red-900">
                            {t("results.title")}
                          </h3>
                          <div className="mb-4 text-4xl font-bold text-red-600">
                            {formatCurrency(calculateLoss())}
                          </div>
                          <p className="text-red-800">
                            {t.rich("results.description", {
                              amount: formatCurrency(calculateLoss()),
                              percentage: riskPercentage[0],
                              strong: (chunks) => <strong>{chunks}</strong>,
                            })}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ) : null}

                {/* Call to Action */}
                {showResults ? (
                  <motion.div
                    className="space-y-4 pt-6 text-center"
                    variants={opacityVariant}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <div className="flex justify-center">
                      <ShieldIcon className="size-8 text-warning" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">
                      {t("callToAction.title")}
                    </h3>
                    <p className="text-muted-foreground">
                      {t("callToAction.description")}
                    </p>
                    <div className="flex flex-col justify-center gap-3 sm:flex-row">
                      <LandingPageButton className="transform gap-2 !rounded-full bg-primary px-6 py-3 text-lg font-bold text-primary-foreground transition-all duration-300 ease-in-out hover:scale-105 hover:gap-4 hover:bg-warning hover:text-primary-foreground">
                        {t("callToAction.button")}
                        <ChevronRightIcon className="ml-2 h-5 w-5" />
                      </LandingPageButton>
                    </div>
                  </motion.div>
                ) : null}
              </div>
            </motion.div>
          </MotionConfig>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <div className="mt-8 text-center">
        <p className="mx-auto max-w-2xl text-sm text-muted-foreground">
          {t("disclaimer")}
        </p>
      </div>
    </div>
  );
}
